import { withManualAiSharingConsent } from '@/store/aiSharingConsent';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore, useModelStore, useAuthStore } from '@/store';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import {
  getToolPickerModels,
  getPreferredToolModel,
  resolveToolPickerModels,
  toolUsesCapabilityCatalogPicker,
  isToolModelPlusLocked,
} from '@/components/Tools/toolDetailHelpers';
import { canAccessPlusOnlyModel, openModelAccessPaywall } from '@/constants/utils/trial';

const EMPTY_ARRAY = [];

/**
 * Compact model-picker for the tool form. Most tools show their curated
 * `defaultModel` + `suggestedModels`. Multi-model tools (AI Model Comparison)
 * merge in the capability-filtered catalog so every text model is listed.
 *
 * Modes:
 *  - single (default): one model. `value` is a modelId string (or null for
 *    the tool's default). `onChange(id)` fires with the new id.
 *  - multi (`multi=true`): up to `maxModels` models. `values` is an ordered
 *    array of modelIds. `onChange(ids[])` fires with the new array.
 */
function ToolModelButton({
  tool,
  value,
  values,
  multi = false,
  maxModels = 4,
  onChange,
  disabled = false,
  /** When true, don't show global-catalog fallbacks until curated models load. */
  deferCatalogFallback = false,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const user = useAuthStore(state => state.user);
  // Pass user into isToolModelPlusLocked for per-model image vs video rules
  const canUseGatedModels = user;

  const fetchAllModels = useModelStore(s => s.fetchAllModels);
  const fetchFeaturedModels = useModelStore(s => s.fetchFeaturedModels);
  const fetchDefaultOutputModels = useModelStore(s => s.fetchDefaultOutputModels);
  const allModelIdsLength = useModelStore(s => s.allModelIds.length);
  const featuredModelIdsLength = useModelStore(s => s.featuredModelIds?.length || 0);
  const isFetchedAllModels = useModelStore(s => s.isFetchedAllModels);

  const curatedModels = useMemo(() => getToolPickerModels(tool), [tool]);
  const usesCatalogPicker = toolUsesCapabilityCatalogPicker(tool);
  const needsCatalogFallback = !!tool && !deferCatalogFallback && (
    usesCatalogPicker || curatedModels.length === 0
  );

  useEffect(() => {
    if (!needsCatalogFallback) return;
    fetchFeaturedModels?.().catch(() => {});
    fetchDefaultOutputModels?.().catch(() => {});
    fetchAllModels?.(1, { autoLoadAll: true }).catch(() => {});
  }, [needsCatalogFallback, fetchAllModels, fetchFeaturedModels, fetchDefaultOutputModels]);

  const pickerModels = useMemo(() => {
    if (!needsCatalogFallback && curatedModels.length) return curatedModels;
    return resolveToolPickerModels(tool, useModelStore.getState());
  }, [
    needsCatalogFallback,
    curatedModels,
    tool,
    allModelIdsLength,
    featuredModelIdsLength,
    isFetchedAllModels,
  ]);

  const pickerModelIdsKey = useMemo(
    () => pickerModels.map(m => m?.id).filter(Boolean).join(','),
    [pickerModels],
  );

  const lastHydratedKeyRef = useRef(null);
  useEffect(() => {
    const key = tool?.slug || tool?.id;
    if (!key || !pickerModelIdsKey) return;
    const hydrateKey = `${key}::${pickerModelIdsKey}`;
    if (lastHydratedKeyRef.current === hydrateKey) return;
    lastHydratedKeyRef.current = hydrateKey;
    useModelStore.getState().addModels(pickerModels);
  }, [tool?.slug, tool?.id, pickerModelIdsKey, pickerModels]);

  const selectedIds = useMemo(() => {
    if (multi) return Array.isArray(values) ? values.filter(Boolean).map(String) : EMPTY_ARRAY;
    return value ? [String(value)] : EMPTY_ARRAY;
  }, [multi, value, values]);

  const defaultModel = getPreferredToolModel(tool) || pickerModels[0] || null;
  const defaultModelId = defaultModel?.id ? String(defaultModel.id) : null;
  const isUsingDefault = !multi && !selectedIds.length;

  const [resolvedModels, setResolvedModels] = useState(EMPTY_ARRAY);
  useEffect(() => {
    if (!selectedIds.length) {
      setResolvedModels(EMPTY_ARRAY);
      return;
    }
    const store = useModelStore.getState();
    const local = pickerModels;
    const resolved = selectedIds.map(id => {
      const fromStore = store.getModelById?.(id);
      if (fromStore) return fromStore;
      return local.find(m => String(m?.id) === id) || null;
    }).filter(Boolean);
    setResolvedModels(resolved);
  }, [selectedIds, pickerModels]);

  const displayModels = resolvedModels.length
    ? resolvedModels
    : (multi ? EMPTY_ARRAY : (defaultModel ? [defaultModel] : EMPTY_ARRAY));

  const isModelSelected = useCallback((model) => {
    const id = String(model?.id || '');
    if (!id) return false;
    if (multi) return selectedIds.includes(id);
    if (selectedIds.length) return selectedIds[0] === id;
    return defaultModelId === id;
  }, [multi, selectedIds, defaultModelId]);

  const handleSelectSingle = useCallback((model, close) => {
    if (isToolModelPlusLocked(model, canUseGatedModels)) {
      close?.();
      const isVideoModel = !!model?.capabilities?.output?.hasVideo;
      openModelAccessPaywall(openModal, {
        model,
        title: isVideoModel
          ? t('nav.modelSelectorDetailed.videoModelsPlus')
          : t('nav.modelSelectorDetailed.unlockAllModels'),
        subtitle: isVideoModel
          ? t('nav.modelSelectorDetailed.videoModelsDesc')
          : t('nav.modelSelectorDetailed.unlockAllDesc'),
      });
      return;
    }
    const id = model?.id ? String(model.id) : null;
    return withManualAiSharingConsent(() => {
      onChange?.(id);
      close?.();
    });
  }, [canUseGatedModels, openModal, t, onChange]);

  const handleToggleMulti = useCallback((model, close) => {
    if (isToolModelPlusLocked(model, canUseGatedModels)) {
      close?.();
      const isVideoModel = !!model?.capabilities?.output?.hasVideo;
      openModelAccessPaywall(openModal, {
        model,
        title: isVideoModel
          ? t('nav.modelSelectorDetailed.videoModelsPlus')
          : t('nav.modelSelectorDetailed.unlockAllModels'),
        subtitle: isVideoModel
          ? t('nav.modelSelectorDetailed.videoModelsDesc')
          : t('nav.modelSelectorDetailed.unlockAllDesc'),
      });
      return;
    }

    const id = model?.id ? String(model.id) : null;
    if (!id) return;

    const cap = Math.max(2, Number(maxModels) || 2);
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : (selectedIds.length >= cap ? [...selectedIds.slice(1), id] : [...selectedIds, id]);

    if (selectedIds.includes(id)) onChange?.(next);
    else return withManualAiSharingConsent(() => onChange?.(next));
  }, [canUseGatedModels, openModal, t, onChange, maxModels, selectedIds]);

  const styles = useMemo(() => StyleSheet.create({
    trigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      minWidth: 0,
      width: '100%',
      ...(Platform.OS === 'web' && { cursor: disabled ? 'not-allowed' : 'pointer' }),
    },
    triggerDisabled: {
      opacity: 0.5,
    },
    logoStack: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
    },
    logoWrap: {
      width: 22,
      height: 22,
      borderRadius: 6,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_block,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      borderWidth: 2,
      borderColor: colors.solid_color_tertiary,
    },
    logoWrapStacked: {
      marginLeft: -8,
    },
    logo: {
      width: '100%',
      height: '100%',
    },
    labelStack: {
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    defaultPill: {
      paddingVertical: 1,
      paddingHorizontal: 6,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
    },
    dropdownContent: {
      minWidth: 260,
      maxWidth: 360,
      maxHeight: 420,
    },
  }), [colors, disabled]);

  const placeholder = multi
    ? (t('pages.tools.chooseModels') || 'Choose models')
    : (t('pages.tools.chooseModel') || 'Choose model');
  const triggerLabel = (() => {
    if (!displayModels.length) return placeholder;
    const names = displayModels.map(m => m?.name).filter(Boolean);
    if (names.length <= 1) return names[0] || placeholder;
    const extra = names.length - 1;
    return `${names[0]} ${(t('pages.tools.modelExtraPicked') || '+%COUNT% more').replace('%COUNT%', extra)}`;
  })();

  const visibleLogos = displayModels.slice(0, 3);

  const triggerNode = (
    <Pressable
      disabled={disabled}
      accessibilityLabel={multi
        ? (t('pages.tools.modelPickerMultiLabel') || 'Pick models to compare')
        : (t('pages.tools.modelPickerLabel') || 'Run with model')}
      style={[
        styles.trigger,
        disabled && styles.triggerDisabled,
      ]}
    >
      <View style={styles.logoStack}>
        {visibleLogos.length === 0 ? (
          <View style={styles.logoWrap}>
            <Icon name='sparkles' size={12} color={colors.text_color_secondary} />
          </View>
        ) : visibleLogos.map((m, i) => (
          <View
            key={m?.id || `logo-${i}`}
            style={[styles.logoWrap, i > 0 && styles.logoWrapStacked]}
          >
            {m?.logoPath ? (
              <Image
                source={{ uri: m.logoPath }}
                style={styles.logo}
                contentFit='cover'
              />
            ) : (
              <Icon name='sparkles' size={12} color={colors.text_color_secondary} />
            )}
          </View>
        ))}
      </View>
      <View style={styles.labelStack}>
        <Text size='s' weight='medium' numberOfLines={1} style={{ flexShrink: 1 }}>
          {triggerLabel}
        </Text>
        {!multi && isUsingDefault && (
          <View style={styles.defaultPill}>
            <Text size='xxs' weight='bold' color='tertiary'>
              {t('pages.tools.defaultModel') || 'Default'}
            </Text>
          </View>
        )}
      </View>
      <Icon name='chevron-down' size={10} color={colors.text_color_secondary} />
    </Pressable>
  );

  if (!pickerModels.length) {
    return triggerNode;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger disabled={disabled}>
        {triggerNode}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align='start'
        side='bottom'
        sideOffset={6}
        style={styles.dropdownContent}
      >
        {pickerModels.map((model) => {
          const plusLocked = isToolModelPlusLocked(model, canUseGatedModels);
          const active = isModelSelected(model);
          const isDefaultOption = defaultModelId && String(model.id) === defaultModelId;
          const name = model.name || model.slug || 'Model';
          const onSelectModel = (close) => {
            if (multi) {
              handleToggleMulti(model, close);
            } else {
              handleSelectSingle(model, close);
            }
          };

          // Native menus only render children whose element type is
          // DropdownMenu.Item, so the wrapper is web-only here. Locked models
          // stay tappable on native so the Plus upsell can open.
          if (Platform.OS !== 'web') {
            const suffix = plusLocked
              ? ' (Plus)'
              : (isDefaultOption ? ` (${t('pages.tools.defaultModel') || 'Default'})` : '');

            return (
              <DropdownMenu.Item key={model.id} onSelect={onSelectModel}>
                {active && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
                <DropdownMenu.ItemTitle>{`${name}${suffix}`}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            );
          }

          return (
            <DropdownMenuItem
              key={model.id}
              title={name}
              subtitle={plusLocked
                ? (t('nav.modelSelectorDetailed.unlockAllModels') || 'Plus')
                : (isDefaultOption ? (t('pages.tools.defaultModel') || 'Default') : undefined)}
              imageUrl={model.logoPath || undefined}
              icon={active ? 'check' : undefined}
              isActive={active}
              isDisabled={plusLocked}
              showInactiveIcon={multi}
              inactiveIcon={active ? 'check' : 'circle'}
              onSelect={onSelectModel}
            />
          );
        })}
        {multi && Platform.OS === 'web' && (
          <DropdownMenuItem
            title={t('common.done') || 'Done'}
            icon='check'
            isSecondary
            onSelect={() => {}}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(ToolModelButton);
